import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { ClientMatterService } from '../../services/client-matter.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { ClientProfileComponent } from '../clientprofile/clientprofile.component';
import { SharingPatientId } from '../sharing-setup/sharing-setup.component';
import { OldPatientOverviewData } from './old-patient-overview.models';

@Injectable()
export class OldPatientOverviewFacade {
  showFacilityRequests = false;
  showDecisionScreen = true; // Show decision screen first
  showSharingSetup = false; // Hide sharing setup until decision is made
  currentPage = 1;
  totalPages = 4;
  // Step 1: Target facility selection
  selectedTargetFacility: SharingPatientId | null = null;
  // Step 2: Facility user selection (multiple selection)
  selectedFacilityUsers: any[] = [];
  // Step 3: Share type selection
  shareTypeSelected: 'document' | 'image' | null = null;
  // Step 4: Send/Receive selection
  shareDirection: 'send' | 'receive' | null = null;
  // Legacy properties for backward compatibility
  selectedPatientId: SharingPatientId | null = null;
  currentFacilityPatientId: SharingPatientId | null = null;
  otherPatientIDs: SharingPatientId[] = [];
  patient: any;
  isImageShareMode = false;
  isIframeLoading = false;
  iframeError: string | null = null;
  currentFacility = '';
  selectedImageShareUrl = '';
  activeImageShareTab: 'pacs' | 'nuage' | 'orthanc' | 'studies' | null = null;
  patientIdsResetToken = 0;

  readonly PACS_URL = 'https://imaging.kindred.com/KH/login';
  readonly NUAGE_URL = 'https://share.nuagedx.com/ImageServer/Pages/Login/Default.aspx';

  ORTHANC_URL = null;

  private dialogRef?: MatDialogRef<any>;
  optionSelected: string;

  constructor(
    private dialog: MatDialog,
    private auth: AuthService,
    private clientMatter: ClientMatterService,
    private router: Router,
    private sessionStorage: SessionStorageService
  ) {}

  getOrthanC_URL(): string {
    return this.auth.userData.getValue()['Orthanc_URL'] || this.ORTHANC_URL;
  }

  initialize(dialogRef: MatDialogRef<any> | null, data: OldPatientOverviewData): void {
    this.ORTHANC_URL = this.getOrthanC_URL();
    this.dialogRef = dialogRef || undefined;
    this.resetState();

    this.patient = data.patient;
    this.currentFacility = this.auth.userData.getValue()['facility'] || '';

    if (this.patient?.metadata?.medical_record_numbers && !this.patient.patientIDs) {
      this.patient.patientIDs = this.patient.metadata.medical_record_numbers;
    }

    this.refreshPatientIdBuckets();

    if (this.dialogRef) {
      this.dialogRef.beforeClosed().subscribe(() => this.dialogRef?.removePanelClass('fullscreen-dialog'));
    }
  }

  // Step 1: Select target facility
  onTargetFacilitySelected(patientId: SharingPatientId): void {
    this.selectedTargetFacility = patientId;
    // Legacy compatibility
    this.selectedPatientId = patientId;
    this.refreshPatientIdBuckets();
    this.clampCurrentPageToAllowed();
    // Auto-advance to next step if valid
    if (this.currentPage === 1) {
      this.onPageChange(2);
    }
  }

  // Step 2: Select facility users (multiple selection)
  onFacilityUserSelected(users: any[]): void {
    this.selectedFacilityUsers = users;
    this.clampCurrentPageToAllowed();
    // Don't auto-advance - let user select multiple users first
  }

  // Step 3: Select share type (DOCUMENT SHARE or IMAGE SHARE)
  onShareTypeSelected(shareType: 'document' | 'image'): void {
    this.shareTypeSelected = shareType;
    this.clampCurrentPageToAllowed();
    // Auto-advance to next step if valid
    if (this.currentPage === 3) {
      this.onPageChange(4);
    }
  }

  // Step 4: Select SEND or RECEIVE - automatically go to fullscreen
  onSendSelected(): void {
    this.shareDirection = 'send';
    this.clampCurrentPageToAllowed();
    // When SEND is clicked with an external URL (handled by share-button component),
    // the external URL opens in a new tab via the extension.
    // We should NOT go to fullscreen mode in this case - the current tab should stay unchanged.
    // Only go to fullscreen if we're NOT opening an external URL.
    // The share-button component handles opening external URLs, so if we reach here,
    // it means the external URL was already opened (or will be opened) in a new tab.
    // For SEND with external URL, we skip fullscreen mode to keep the current tab on patient overview.
    if (this.shareTypeSelected === 'image') {
      // Don't go to fullscreen mode when SEND opens external URL
      // The external URL is already opened in a new tab by the share-button component
      // Just stay on the current page
      console.log('[Patient Overview] SEND selected - external URL opened in new tab, staying on current page');
    } else if (this.shareTypeSelected === 'document') {
      // For document share, open the client profile
      this.onDocumentShare();
    }
  }

  async onReceiveSelected(): Promise<void> {
    this.shareDirection = 'receive';
    this.clampCurrentPageToAllowed();
    // Automatically go to fullscreen view based on share type
    if (this.shareTypeSelected === 'image') {
      // Direct access to studies page for 'receive' (Retrieve)
      // Skipping all Orthanc/Keycloak auth logic as we are replacing it with StudiesPageComponent
      await this.goToFullscreenMode();
    } else if (this.shareTypeSelected === 'document') {
      // For document share, open the client profile
      this.onDocumentShare();
    }
  }

  // Legacy methods for backward compatibility
  onSelectedPatientIdEvent(patientId: SharingPatientId): void {
    this.onTargetFacilitySelected(patientId);
  }

  onSharingFacilitySelected(patientId: SharingPatientId): void {
    this.onTargetFacilitySelected(patientId);
  }

  onShareWithFacility(): void {
    this.shareTypeSelected = 'image';
    this.shareDirection = 'send';
    this.selectedTargetFacility = null;
    this.selectedFacilityUsers = [];
    this.patientIdsResetToken++;
    this.clampCurrentPageToAllowed();
  }

  onShareFromFacility(): void {
    this.shareTypeSelected = 'image';
    this.shareDirection = 'receive';
    this.selectedTargetFacility = null;
    this.selectedFacilityUsers = [];
    this.patientIdsResetToken++;
    this.clampCurrentPageToAllowed();
  }

  onSharingGoNext(): void {
    // Legacy method - now handled automatically in onSendSelected/onReceiveSelected
    this.goToFullscreenMode();
  }

  onClose(): void {
    this.dialogRef?.removePanelClass('fullscreen-dialog');
    this.isImageShareMode = false;
    this.isIframeLoading = false;
    this.iframeError = null;
    this.selectedImageShareUrl = '';
    this.activeImageShareTab = null;
    // Don't close if client profile is open - let it handle its own lifecycle
    if (!this.dialog.openDialogs.some(d => d.id === 'client-profile')) {
      this.dialogRef?.close();
    }
  }

  async onDocumentShare(): Promise<void> {
    // Set share type and handle step advancement
    this.onShareTypeSelected('document');

    this.patient =
      !this.patient || Object.keys(this.patient).length === 1 ? this.auth.getPatientProfile() : this.patient;

    if (!this.patient.caseName && this.patient.LegalCaseId) {
      try {
        const completePatientData = await this.clientMatter.getPatientData(this.patient.LegalCaseId);
        this.patient = { ...this.patient, ...completePatientData };
      } catch (error) {
        console.warn('Could not load complete patient data:', error);
      }
    }

    if (this.patient?.metadata?.medical_record_numbers && !this.patient.patientIDs) {
      this.patient.patientIDs = this.patient.metadata.medical_record_numbers;
    }

    this.refreshPatientIdBuckets();

    // Update URL to patient profile route using Angular router
    const patientID = this.patient?.caseName || this.patient?.patientId || '';
    if (patientID) {
      // Use router to update URL - this ensures Angular router state is also updated
      // We use replaceUrl: true to replace current history entry
      // The route doesn't render a component (it was removed from routing), so this just updates the URL
      this.router
        .navigateByUrl(`/patient-profile/${patientID}`, { replaceUrl: true, skipLocationChange: false })
        .then(() => {
          console.log('Updated URL to /patient-profile/', patientID);
        })
        .catch(err => {
          console.error('Failed to update URL:', err);
          // Fallback: use history API if router navigation fails
          window.history.replaceState({}, '', `/patient-profile/${patientID}`);
        });
    }

    // Open the client profile immediately when DOCUMENT is clicked
    const dialogConfig: MatDialogConfig = {
      width: '95%',
      minWidth: '95%',
      maxWidth: '95%',
      height: '90%',
      id: 'client-profile',
      data: { patient: this.patient },
      disableClose: false
    };

    // Open client profile dialog after navigation
    const clientProfileDialogRef = this.dialog.open(ClientProfileComponent, dialogConfig);

    // Close the old-patient-overview dialog
    // Use a delay to ensure client profile dialog is initialized
    setTimeout(() => {
      if (this.dialogRef) {
        // Close without triggering navigation
        this.dialogRef.close({ skipNavigation: true });
      }
    }, 100);
  }

  // FIXME: I am willing to remove this method. This is a pending tasks.
  async goToFullscreenMode(targetTab?: 'pacs' | 'nuage' | 'orthanc' | 'studies'): Promise<void> {
    // Only proceed to fullscreen if IMAGE SHARE is selected
    if (this.shareTypeSelected !== 'image') {
      return;
    }

    // Determine which URL to show based on share direction
    // SEND → PACS URL, RECEIVE → STUDIES (was Orthanc)
    let resolvedTab: 'pacs' | 'nuage' | 'orthanc' | 'studies';
    if (targetTab) {
      resolvedTab = targetTab;
    } else if (this.shareDirection === 'send') {
      resolvedTab = 'pacs';
    } else if (this.shareDirection === 'receive') {
      resolvedTab = 'studies';
    } else {
      // Fallback to legacy logic
      resolvedTab = 'pacs';
    }

    this.isImageShareMode = true;
    this.isIframeLoading = true;
    this.dialogRef?.addPanelClass('fullscreen-dialog');
    this.dialogRef?.updatePosition({ top: '0px', left: '0px', right: '0px', bottom: '0px' });
    this.dialogRef?.updateSize('100vw', '100vh');

    this.activeImageShareTab = resolvedTab;

    if (resolvedTab === 'studies') {
      // Studies page is a component, not an iframe
      this.isIframeLoading = false;
      this.iframeError = null;
      // No URL needed
    } else if (resolvedTab === 'orthanc') {
      try {
        console.log('[Patient Overview] Setting up Orthanc iframe...');
        this.iframeError = null;

        // OPTION 2: Use pre-authenticated URL if available (from onReceiveSelected)
        // This avoids triggering authentication during fullscreen mode
        if (this.selectedImageShareUrl) {
          console.log('[Patient Overview] Using pre-authenticated Orthanc URL (no redirect will occur)');
          // URL is already set from pre-authentication, just clear loading state
          this.isIframeLoading = false;
        } else {
          // Fallback: If URL not pre-authenticated, get it now
          // This should only happen if goToFullscreenMode is called directly (not via onReceiveSelected)
          console.warn('[Patient Overview] Orthanc URL not pre-authenticated, getting it now (may cause redirect)');

          // Store route for return URL
          const currentPathname = window.location.pathname;
          if (currentPathname.includes('patient-overview-modal')) {
            this.sessionStorage.setReturnUrl(currentPathname);
          }

          // This will redirect to login if not authenticated
          try {
          } catch (error) {
            if (error instanceof Error && error.message.includes('Callback processing in progress')) {
              console.log('[Patient Overview] Keycloak instance initializing, will retry after a short delay...');
              // Wait a bit for Keycloak to initialize, then retry
              await new Promise(resolve => setTimeout(resolve, 2000));
              try {
                // Retry once - instance should be ready now
              } catch (retryError) {
                // If retry also fails, show error but keep loading state
                // The instance might still be initializing - user can wait or refresh
                console.warn('[Patient Overview] Still initializing after retry, showing loading state');
                this.iframeError = 'Initializing authentication... Please wait.';
                // Don't clear loading state - let user see it's still working
                return; // Exit early, will retry when user interacts or page refreshes
              }
            } else if (error instanceof Error && error.message.includes('invalid')) {
              // Token is invalid - show user-friendly error message
              console.error('[Patient Overview] Authentication token is invalid:', error.message);
              this.iframeError = 'Authentication failed. Please refresh the page and try again.';
              this.isIframeLoading = false; // Stop loading to show error
              return; // Exit early
            } else {
              throw error;
            }
          }
          this.isIframeLoading = false;
        }

        console.log('[Patient Overview] Orthanc URL ready:', this.selectedImageShareUrl);

        // Ensure URL is set
        if (!this.selectedImageShareUrl) {
          this.iframeError = 'Failed to get Orthanc URL';
          this.isIframeLoading = false;
          return;
        }
      } catch (error: any) {
        console.error('[Patient Overview] Error setting up Orthanc iframe:', error);
        this.isIframeLoading = false;

        // Handle different error types
        if (error?.message?.includes('not logged in') || error?.message?.includes('redirecting to login')) {
          this.iframeError = 'Please log in to access Orthanc. Redirecting...';
          // The login redirect will happen automatically
        } else if (error?.message?.includes('Keycloak library not loaded')) {
          this.iframeError = 'Keycloak authentication is not available. Please check your configuration.';
        } else {
          this.iframeError = error?.message || 'Failed to load Orthanc. Please try again.';
        }
      }
    } else {
      this.selectedImageShareUrl = resolvedTab === 'pacs' ? this.PACS_URL : this.NUAGE_URL;
      console.log('[Patient Overview] Using URL:', this.selectedImageShareUrl);
    }
  }

  onImageShare(): void {
    // Set share type and handle step advancement
    this.onShareTypeSelected('image');
    // Clear direction when share type changes
    this.shareDirection = null;
    // Don't clear facility/user selections - they should persist
  }

  onPageChange(page: number): void {
    const maxAllowed = this.getMaxAllowedStep();
    const targetPage = page <= maxAllowed ? page : maxAllowed;
    this.currentPage = targetPage;
  }

  onRestartSharingSetup(): void {
    this.dialogRef?.removePanelClass('fullscreen-dialog');
    this.dialogRef?.updateSize();
    this.dialogRef?.updatePosition();

    this.isImageShareMode = false;
    this.isIframeLoading = false;
    this.iframeError = null;
    this.selectedImageShareUrl = '';
    this.activeImageShareTab = null;
    this.shareDirection = null;
    this.selectedTargetFacility = null;
    this.selectedFacilityUsers = [];
    this.shareTypeSelected = null;
    // Legacy compatibility
    this.selectedPatientId = null;
    this.currentPage = 1;
    this.patientIdsResetToken++;
    // Reset to decision screen
    this.showDecisionScreen = true;
    this.showSharingSetup = false;
    this.clampCurrentPageToAllowed();
  }

  getOptionSelectedTitle(): string {
    return this.optionSelected === 'exchange'
      ? 'Images and Documents Exchange'
      : this.optionSelected === 'profile'
        ? 'Patient Profile'
        : 'Patient Library';
  }

  onDecisionSelected(option: 'profile' | 'library' | 'exchange'): void {
    this.optionSelected = option;
    if (option === 'exchange') {
      this.showDecisionScreen = false;
      this.showSharingSetup = true;
    }
    // Options 1 and 2 (profile and library) are disabled for now
  }

  onIframeLoaded(): void {
    this.isIframeLoading = false;
    console.log('[Patient Overview] Iframe loaded successfully');
  }

  get activeImageShareTitle(): string {
    if (this.activeImageShareTab === 'pacs') {
      return 'IMPACT System';
    }
    if (this.activeImageShareTab === 'orthanc') {
      return 'Orthanc PACS Viewer';
    }
    if (this.activeImageShareTab === 'nuage') {
      return 'NuageDx Clear Canvas';
    }
    if (this.activeImageShareTab === 'studies') {
      return 'Patient Studies';
    }
    return '';
  }

  private refreshPatientIdBuckets(): void {
    const patientIds: SharingPatientId[] = Array.isArray(this.patient?.patientIDs) ? this.patient.patientIDs : [];

    console.log('[Patient Overview] Refreshing patient ID buckets:', {
      patientIds,
      currentFacility: this.currentFacility,
      patientData: this.patient
    });

    if (!patientIds.length) {
      console.warn('[Patient Overview] No patient IDs found for patient');
      this.currentFacilityPatientId = null;
      this.otherPatientIDs = [];
      return;
    }

    this.currentFacilityPatientId = patientIds.find(id => id.facility === this.currentFacility) || null;
    this.otherPatientIDs = patientIds.filter(id => id.facility !== this.currentFacility);

    console.log('[Patient Overview] Patient ID buckets refreshed:', {
      currentFacilityPatientId: this.currentFacilityPatientId,
      otherPatientIDs: this.otherPatientIDs,
      otherPatientIDsCount: this.otherPatientIDs.length
    });
  }

  private getMaxAllowedStep(): number {
    let max = 1; // Step 1: Select target facility

    // Step 1 completed: Can access Step 2 (Select facility user)
    if (this.selectedTargetFacility) {
      max = Math.max(max, 2);
    }

    // Step 2 completed: Can access Step 3 (Select share type)
    // Require at least one user selected
    if (this.selectedFacilityUsers && this.selectedFacilityUsers.length > 0) {
      max = Math.max(max, 3);
    }

    // Step 3 completed: Can access Step 4 (Select SEND/RECEIVE)
    if (this.shareTypeSelected) {
      max = Math.max(max, 4);
    }

    // Step 4 is the last step - selecting SEND/RECEIVE goes directly to fullscreen
    // No Step 5 anymore

    return max;
  }

  private clampCurrentPageToAllowed(): void {
    const allowed = this.getMaxAllowedStep();
    if (this.currentPage > allowed) {
      this.currentPage = allowed;
    }
  }

  private resetState(): void {
    this.showFacilityRequests = false;
    this.showDecisionScreen = true;
    this.showSharingSetup = false;
    this.currentPage = 1;
    this.totalPages = 4;
    this.selectedTargetFacility = null;
    this.selectedFacilityUsers = [];
    this.shareTypeSelected = null;
    this.shareDirection = null;
    // Legacy compatibility
    this.selectedPatientId = null;
    this.currentFacilityPatientId = null;
    this.otherPatientIDs = [];
    this.isImageShareMode = false;
    this.isIframeLoading = false;
    this.iframeError = null;
    this.selectedImageShareUrl = '';
    this.activeImageShareTab = null;
    this.patientIdsResetToken = 0;
  }
}
